import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatNumber, formatDate, daysLeft } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from "recharts";
import {
  ClipboardList, Wallet, AlertCircle, Cpu, Plus, Calculator, KanbanSquare,
  TrendingUp, Crown, AlertTriangle,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, sub, tint, testid }) => (
  <Card data-testid={testid} className="p-5 border-slate-200/80 shadow-sm animate-fade-up">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</p>
        <p className="mt-2 font-heading text-2xl font-extrabold text-slate-900">{value}</p>
        {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
      </div>
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${tint}`}><Icon className="h-5 w-5" /></div>
    </div>
  </Card>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="text-slate-400">Memuat dashboard...</div>;

  const maxBar = Math.max(...data.status_dist.map((s) => s.count), 1);
  const barColors = ["#a855f7","#64748b","#2563eb","#06b6d4","#14b8a6","#10b981"];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">Dashboard Usaha</h1>
          <p className="text-sm text-slate-500">Ringkasan operasional workshop bordir Anda hari ini.</p>
        </div>
        <div className="flex flex-wrap gap-2 no-print">
          {(user?.role === "admin" || user?.role === "kasir") && (
            <Button data-testid="qa-new-order" className="bg-blue-600 hover:bg-blue-700" onClick={() => navigate("/order", { state: { openNew: true } })}>
              <Plus className="mr-2 h-4 w-4" /> Terima Pesanan Baru
            </Button>
          )}
          <Button data-testid="qa-calculator" variant="outline" onClick={() => navigate("/kalkulator")}>
            <Calculator className="mr-2 h-4 w-4" /> Kalkulator Stitch
          </Button>
          <Button data-testid="qa-queue" variant="outline" onClick={() => navigate("/produksi")}>
            <KanbanSquare className="mr-2 h-4 w-4" /> Cek Antrean
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard testid="stat-active-orders" icon={ClipboardList} label="Pesanan Aktif" value={formatNumber(data.active_orders)} sub="Sedang dalam produksi" tint="bg-blue-50 text-blue-600" />
        <StatCard testid="stat-revenue" icon={Wallet} label="Pendapatan Bulan Ini" value={formatIDR(data.revenue_month)} sub="Total DP & pelunasan masuk" tint="bg-emerald-50 text-emerald-600" />
        <StatCard testid="stat-pending" icon={AlertCircle} label="Sisa Tagihan Pending" value={formatIDR(data.pending_payments)} sub="DP & Belum Bayar" tint="bg-amber-50 text-amber-600" />
        <StatCard testid="stat-machines" icon={Cpu} label="Mesin Beroperasi" value={`${data.machines_running}/${data.machines_total}`} sub="Mesin bordir aktif" tint="bg-cyan-50 text-cyan-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-5 border-slate-200/80 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-slate-900">Tren Pendapatan</h2>
            <TrendingUp className="h-5 w-5 text-blue-600" />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.revenue_trend}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Area type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2.5} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 border-slate-200/80 shadow-sm">
          <h2 className="font-heading font-bold text-slate-900 mb-4 flex items-center gap-2"><Crown className="h-5 w-5 text-amber-500" /> Pelanggan Teratas</h2>
          <div className="space-y-3">
            {data.top_customers.map((c, i) => (
              <div key={c.name} className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800">{c.name}</p>
                  <p className="text-xs text-slate-500">{c.orders} order · {formatIDR(c.spend)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-5 border-slate-200/80 shadow-sm">
          <h2 className="font-heading font-bold text-slate-900 mb-4">Distribusi Status Produksi</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.status_dist}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="status" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={0} angle={-12} textAnchor="end" height={50} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {data.status_dist.map((e, i) => <Cell key={i} fill={barColors[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 border-slate-200/80 shadow-sm">
          <h2 className="font-heading font-bold text-slate-900 mb-3 flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-rose-500" /> Stok Menipis</h2>
          {data.low_stock.length === 0 ? (
            <p className="text-sm text-slate-400">Semua stok aman 👍</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {data.low_stock.map((s, i) => (
                <div key={i} data-testid="badge-low-stock-alert" className="flex items-center justify-between rounded-lg bg-rose-50 border border-rose-200 px-3 py-2">
                  <span className="text-sm text-rose-800 truncate">{s.name}</span>
                  <span className="text-xs font-bold text-rose-700 whitespace-nowrap">{s.stock}/{s.min} {s.unit}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-5 border-slate-200/80 shadow-sm">
        <h2 className="font-heading font-bold text-slate-900 mb-4">Order Terbaru</h2>
        <div className="space-y-2">
          {data.recent_orders.map((o) => {
            const dl = daysLeft(o.target_date);
            return (
              <div key={o.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50/50 px-4 py-3">
                <div className="min-w-0">
                  <p className="font-mono text-xs text-blue-600">{o.order_id}</p>
                  <p className="text-sm font-semibold text-slate-800">{o.customer_name} · <span className="text-slate-500 font-normal">{o.design_name}</span></p>
                </div>
                <div className="flex items-center gap-2">
                  {dl !== null && dl <= 2 && o.status !== "Selesai" && (
                    <span className="text-xs font-semibold text-rose-600">{dl < 0 ? "Lewat target" : `${dl} hari lagi`}</span>
                  )}
                  <StatusBadge value={o.payment_status} kind="payment" />
                  <StatusBadge value={o.status} />
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
